import React from 'react';
import './invoice-mascot.style.scss';

export type MascotExpression = 'idle' | 'sad' | 'happy' | 'confused';

/** Invozio logo worn on the mascot's chest badge by default (brands the
 *  character across every place it appears). Pass a different `logoSrc`
 *  to override, or `null`/'' to fall back to the plain "invoice done" chip. */
const DEFAULT_MASCOT_LOGO = '/assets/svg/invozio-logo.svg';

interface InvoiceMascotProps {
  /** Facial expression. */
  expression?: MascotExpression;
  /** Ref to the eyes group so a consumer can make the eyes follow the cursor. */
  eyesRef?: React.Ref<SVGGElement>;
  /** Extra class on the root <svg>. */
  className?: string;
  /** Image shown on the chest badge — defaults to the Invozio logo so every
   *  mascot is branded. Pass '' / null to show the plain "invoice done" chip. */
  logoSrc?: string | null;
  /** When true, the mouth animates (open/close) so the mascot looks like it's
   *  talking. */
  talking?: boolean;
}

/**
 * Shared Invozio tour-guide mascot — a self-contained, animated SVG character.
 * Idle animations (bob, wave, blink, antenna pulse) run from its stylesheet.
 */
export const InvoiceMascot: React.FC<InvoiceMascotProps> = ({ expression = 'idle', eyesRef, className, logoSrc = DEFAULT_MASCOT_LOGO, talking = false }) => {
  const sad = expression === 'sad';
  const happy = expression === 'happy';
  const confused = expression === 'confused';
  return (
    <svg className={`iv-bot${className ? ` ${className}` : ''}`} viewBox="0 0 120 132" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="ivbotHead" x1="30" y1="18" x2="92" y2="76" gradientUnits="userSpaceOnUse">
          <stop stopColor="#9d6bff" />
          <stop offset="1" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id="ivbotBody" x1="40" y1="70" x2="80" y2="112" gradientUnits="userSpaceOnUse">
          <stop stopColor="#b57cff" />
          <stop offset="1" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id="ivbotArm" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#a855f7" />
          <stop offset="1" stopColor="#8b3ff0" />
        </linearGradient>
        <radialGradient id="ivbotVisor" cx="0.5" cy="0.3" r="0.85">
          <stop stopColor="#3b246b" />
          <stop offset="1" stopColor="#241243" />
        </radialGradient>
        <radialGradient id="ivbotGloss" cx="0.5" cy="0.2" r="0.75">
          <stop stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ground shadow */}
      <ellipse className="iv-bot-shadow" cx="60" cy="124" rx="27" ry="5" fill="#7c3aed" opacity="0.18" />

      <g className="iv-bot-float">
        {/* antenna */}
        <line x1="60" y1="21" x2="60" y2="11" stroke="#c084fc" strokeWidth="3" strokeLinecap="round" />
        <circle className="iv-bot-antenna" cx="60" cy="8" r="5" fill="#e9d5ff" />
        <circle cx="60" cy="8" r="2" fill="#fff" />

        {/* feet */}
        <rect x="45" y="103" width="14" height="9" rx="4.5" fill="#6d28d9" />
        <rect x="61" y="103" width="14" height="9" rx="4.5" fill="#6d28d9" />

        {/* left arm (static) */}
        <rect x="27" y="80" width="9" height="20" rx="4.5" fill="url(#ivbotArm)" />
        <circle cx="31.5" cy="101" r="5.5" fill="#c084fc" />

        {/* waving arm — hinged at the shoulder (top of its box) */}
        <g className="iv-bot-arm">
          <rect x="84" y="80" width="9" height="20" rx="4.5" fill="url(#ivbotArm)" />
          <circle cx="88.5" cy="101" r="5.5" fill="#c084fc" />
        </g>

        {/* body */}
        <rect x="39" y="70" width="42" height="38" rx="16" fill="url(#ivbotBody)" />
        <rect x="44" y="74" width="32" height="12" rx="6" fill="#fff" opacity="0.16" />
        {/* chest chip — Invozio logo badge (branded), or the default
            "invoice done" chip when no logo is provided */}
        <rect x="46" y="87" width="28" height="15" rx="6" fill="#fbf9ff" />
        {logoSrc ? (
          <image href={logoSrc} x="48.5" y="90.2" width="23" height="8.6" preserveAspectRatio="xMidYMid meet" />
        ) : (
          <>
            <circle cx="55" cy="94.5" r="3.4" fill="#22c55e" />
            <path d="M53.6 94.6l1 1 1.9 -2.1" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <rect x="61" y="91.5" width="7" height="1.8" rx="0.9" fill="#c4b5fd" />
            <rect x="61" y="95.5" width="5" height="1.8" rx="0.9" fill="#ddd6fe" />
          </>
        )}

        {/* headphone ears */}
        <rect x="20" y="40" width="9" height="18" rx="4.5" fill="#6d28d9" />
        <circle cx="24.5" cy="49" r="2.4" fill="#c084fc" />
        <rect x="91" y="40" width="9" height="18" rx="4.5" fill="#6d28d9" />
        <circle cx="95.5" cy="49" r="2.4" fill="#c084fc" />

        {/* head shell + top gloss */}
        <rect x="27" y="19" width="66" height="55" rx="22" fill="url(#ivbotHead)" />
        <rect x="34" y="23" width="42" height="16" rx="8" fill="url(#ivbotGloss)" />

        {/* glossy dark visor */}
        <rect x="35" y="29" width="50" height="34" rx="15" fill="url(#ivbotVisor)" />
        <rect x="38" y="31" width="44" height="12" rx="8" fill="url(#ivbotGloss)" opacity="0.4" />

        {/* eyes — glowing; wrapped so they shift together to follow the cursor */}
        <g className="iv-bot-eyes" ref={eyesRef}>
          <circle className="iv-bot-eye" cx="50" cy="45" r="5" fill="#fff" />
          <circle className="iv-bot-eye" cx="70" cy="45" r="5" fill="#fff" />
          <circle cx="51.6" cy="43.4" r="1.6" fill="#e9d5ff" />
          <circle cx="71.6" cy="43.4" r="1.6" fill="#e9d5ff" />
        </g>
        {/* eyebrows — sad: both inner-raised (worried); confused: quizzical (one up, one down) */}
        {sad && (
          <>
            <path d="M46 41l7 -3" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" />
            <path d="M74 41l-7 -3" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" />
          </>
        )}
        {confused && (
          <>
            <path d="M46 38l7 -1" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" />
            <path d="M74 40l-7 2" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" />
          </>
        )}
        {/* cheeks */}
        <circle cx="44" cy="53" r="2.6" fill="#f472b6" opacity="0.75" />
        <circle cx="76" cy="53" r="2.6" fill="#f472b6" opacity="0.75" />
        {/* mouth — frown (sad), grin (happy), wavy (confused), smile (idle) */}
        <path
          className={`iv-bot-mouth${talking ? ' iv-bot-mouth--talk' : ''}`}
          d={sad
            ? 'M54 55c2 -2.6 10 -2.6 12 0'
            : happy
              ? 'M52 51c3 5 13 5 16 0z'
              : confused
                ? 'M54 54q3 -3 6 0t6 0'
                : 'M54 53c2 2.4 10 2.4 12 0'}
          stroke="#ffffff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill={happy ? '#ffffff' : 'none'}
        />
      </g>
    </svg>
  );
};

export default InvoiceMascot;
